import { Box, Divider, Stack } from '@mui/material';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { PointOfInterest } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useFeatureFlag } from '@/hooks';
import { ReviewAction } from '@/components/Analysis/ReviewScreen/PointsOfInterest/SummaryCard/ReviewAction';
import { ReviewStatus } from '@/components/Analysis/ReviewScreen/PointsOfInterest/SummaryCard/ReviewStatus';
import { LocationTag } from '@/constants';

type Props = {
  selectedPoiInfo: PointOfInterest;
};

export const PoiReviewControls = ({ selectedPoiInfo }: Props) => {
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);

  const templateOfSelectedPoi = useMemo(
    () => allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo?.template?.id),
    [allPointOfInterestTemplates, selectedPoiInfo?.template?.id]
  );
  const hasReviewActionField = useMemo(
    () => templateOfSelectedPoi?.fields?.some(({ key }) => key === 'review_action'),
    [templateOfSelectedPoi?.fields]
  );
  const hasInternalStatusField = useMemo(
    () => templateOfSelectedPoi?.fields?.some(({ key }) => key === 'internal_review_status'),
    [templateOfSelectedPoi?.fields]
  );

  const showReviewAction = canViewReviewScreen && hasReviewActionField;
  const showReviewStatus = isAITEngineer && hasInternalStatusField;

  if (!showReviewAction && !showReviewStatus) {
    return null;
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Stack
        direction="row"
        gap={1.2}
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          px: '1.4rem',
          pb: 1.5,
          pt: 0,
          flexWrap: 'nowrap',
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {showReviewAction && (
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <ReviewAction
              pointOfInterest={selectedPoiInfo}
              templateOfSelectedPoi={templateOfSelectedPoi}
              location={LocationTag.ViewerSelectedPoiOverview}
            />
          </Box>
        )}
        {showReviewStatus && (
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <ReviewStatus
              pointOfInterest={selectedPoiInfo}
              templateOfSelectedPoi={templateOfSelectedPoi}
              location={LocationTag.ViewerSelectedPoiOverview}
            />
          </Box>
        )}
      </Stack>
      <Divider sx={{ mx: '1.4rem' }} />
    </Box>
  );
};
